import React from 'react';

import { Divider, Skeleton, Stack, Title, Group, createStyles } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import usePastDuePayments from 'hooks/reports/payments/usePastDuePayments';
import usePendingPayments from 'hooks/reports/payments/usePendingPayments';
import { useNavigate } from 'react-router-dom';

import { useDashboardStyles } from '../styles';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
      "pending pastDue"
      `,
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto',
      gridTemplateAreas: `
      "pending"
      "pastDue"
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto',
      gridTemplateAreas: `
      "pending pastDue"
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto',
      gridTemplateAreas: `
      "pending"
      "pastDue"
      `,
    },
  },
  pending: {
    gridArea: 'pending',
  },
  pastDue: {
    gridArea: 'pastDue',
  },
}));

const PaymentsCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { formatCurrency } = useLocalization();
  const { hasPerm } = useAuth();
  const isPaymentsViewable = hasPerm('PaymentReportsView');

  const {
    data: pendingData,
    isLoading: pendingIsLoading,
    isError: pendingIsError,
    isSuccess: pendingIsSuccess,
  } = usePendingPayments({ enabled: isPaymentsViewable });
  const {
    data: pastDueData,
    isLoading: pastDueIsLoading,
    isError: pastDueIsError,
    isSuccess: pastDueIsSuccess,
  } = usePastDuePayments({ enabled: isPaymentsViewable });

  const handleReportClick = (report: string): void => {
    if (isPaymentsViewable) {
      navigate(`/reports/${report}`);
    }
  };

  const getPendingCount = (): JSX.Element => {
    if (!isPaymentsViewable) {
      return <IconLock size='2rem' />;
    }
    if (pendingIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (pendingIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (pendingIsSuccess && pendingData) {
      return <div className={dashboard.value}>{pendingData.data.count}</div>;
    }
    return <></>;
  };

  const getPendingSum = (): JSX.Element => {
    if (!isPaymentsViewable) {
      return <IconLock size='2rem' />;
    }
    if (pendingIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (pendingIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (pendingIsSuccess && pendingData) {
      return <div className={dashboard.value}>{formatCurrency(pendingData.data.total / 100)}</div>;
    }
    return <></>;
  };

  const getPastDueCount = (): JSX.Element => {
    if (!isPaymentsViewable) {
      return <IconLock size='2rem' />;
    }
    if (pastDueIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (pastDueIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (pastDueIsSuccess && pastDueData) {
      return <div className={dashboard.value}>{pastDueData.data.count}</div>;
    }
    return <></>;
  };

  const getPastDueSum = (): JSX.Element => {
    if (!isPaymentsViewable) {
      return <IconLock size='2rem' />;
    }
    if (pastDueIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (pastDueIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (pastDueIsSuccess && pastDueData) {
      return <div className={dashboard.value}>{formatCurrency(pastDueData.data.total / 100)}</div>;
    }
    return <></>;
  };

  return (
    <div className={`${dashboard.payments} ${dashboard.card}`}>
      <div className={dashboard.cardHeader}>
        <Title order={4}>Payments</Title>
      </div>

      <Divider />

      <div className={classes.grid}>
        <Group
          noWrap
          spacing='xs'
          className={`${classes.pending} ${isPaymentsViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('pending-payments')}
        >
          <Stack spacing={0} p='xs'>
            <div className={dashboard.label}>Pending</div>
            {getPendingCount()}
          </Stack>
          <Stack spacing={0} p='xs'>
            <div className={dashboard.label}>Total Amount</div>
            {getPendingSum()}
          </Stack>
        </Group>

        <Group
          noWrap
          spacing='xs'
          className={`${classes.pastDue} ${isPaymentsViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('past-due-payments')}
        >
          <Stack spacing={0} p='xs'>
            <div className={dashboard.label}>Past Due</div>
            {getPastDueCount()}
          </Stack>
          <Stack spacing={0} p='xs'>
            <div className={dashboard.label}>Total Amount</div>
            {getPastDueSum()}
          </Stack>
        </Group>
      </div>
    </div>
  );
};

export default PaymentsCard;
